/** The floating status pill Kelly shows above the composer, and the two motion primitives that go in it.
 *  Both animations are transform/opacity only: an indeterminate `CircularProgress` animates `stroke-dashoffset`,
 *  which cannot be composited and costs a style recalc + paint + raster every frame it is on screen. */

import { keyframes, styled } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { SCROLLBAR_GUTTER, TRANSCRIPT_CHROME_BOTTOM } from "./composer-chrome"

const spin = keyframes({ from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } })
const pulse = keyframes({ "0%,100%": { opacity: 1 }, "50%": { opacity: 0.35 } })
const calmPulse = keyframes({ "0%,100%": { opacity: 1 }, "50%": { opacity: 0.7 } })

/** For the pinned cue only: reduced motion stops its spinner, so opt back into a gentler pulse as the one
 *  remaining progress signal. The in-transcript trail head scrolls away and needs no such exception. */
export const CALM_PULSE_SX = {
  "@media (prefers-reduced-motion: reduce)": { animation: `${calmPulse} 3s ease-in-out infinite` },
} satisfies SxProps<Theme>

/** One rect for both floating pills: the working cue and the reattach indicator occupy it one at a time, and
 *  `maxWidth` reserves the right of the band for the jump-to-latest control. */
export const FLOATING_PILL_SX = {
  position: "absolute",
  insetInline: SCROLLBAR_GUTTER,
  bottom: TRANSCRIPT_CHROME_BOTTOM,
  marginInline: "auto",
  width: "fit-content",
  maxWidth: "calc(100% - 6rem)",
  zIndex: 2,
  pointerEvents: "none",
} satisfies SxProps<Theme>

export const ChatStatusPill = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
  padding: theme.spacing(0.75, 1.5),
  borderRadius: "999px",
  border: "1px solid var(--opcolor-grey-lighter)",
  backgroundColor: "var(--app-bg-paper)",
  boxShadow: "var(--mui-shadows-2)",
  color: "var(--app-text-secondary)",
  maxWidth: "100%",
  minWidth: 0,
}))

export const ChatStatusSpinner = styled("span")({
  width: "0.875rem",
  height: "0.875rem",
  flexShrink: 0,
  borderRadius: "50%",
  border: "2px solid var(--opcolor-grey-lighter)",
  borderTopColor: "var(--app-text-secondary)",
  animation: `${spin} 0.8s linear infinite`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
})

/** Animation only — the in-transcript trail head uses this too and must stay free to wrap. */
export const WorkingPulse = styled("span", { shouldForwardProp: (prop) => prop !== "pulsing" })<{ pulsing: boolean }>(
  ({ pulsing }) => ({
    minWidth: 0,
    ...(pulsing ? { animation: `${pulse} 1.4s ease-in-out infinite` } : {}),
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
  }),
)
